import { useMutation, useQueryClient } from "@tanstack/react-query";
import { titleQueryKeys } from "@/lib/react-query/keyFactory";
import {
  proofingTitleArchive,
  proofingTitleCreate,
  proofingTitleDelete,
  proofingTitleUpdate,
} from "@/proofingService-client/sdk.gen";
import type {
  ProofingTitleCreateData,
  ProofingTitleCreateResponse,
  ProofingTitleUpdateData,
} from "@/proofingService-client/types.gen";

export interface UseCreateProofingTitleOptions {
  onSuccess?: (data: ProofingTitleCreateResponse) => void;
  onError?: (error: Error) => void;
}

export const useCreateProofingTitle = (options?: UseCreateProofingTitleOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ProofingTitleCreateData["body"]) => {
      const response = await proofingTitleCreate({
        body: data,
      });
      if (!response.data) {
        throw new Error("Failed to create proofing title");
      }
      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate and refetch titles list to show the new title
      queryClient.invalidateQueries({ queryKey: titleQueryKeys.all() });
      options?.onSuccess?.(data);
    },
    onError: (error: Error) => {
      console.error("Failed to create proofing title:", error);
      options?.onError?.(error);
    },
  });
};

export interface UseUpdateProofingTitleOptions {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}

/**
 * Hook for updating an existing proofing title with auto-save functionality.
 *
 * Supports optimistic updates and automatic query cache invalidation.
 * The mutation accepts partial updates - only fields that need to be changed
 * should be included in the update data.
 *
 * @param titleId - The ID of the title to update
 * @param options - Optional callbacks for success and error handling
 *
 * @example
 * ```tsx
 * const updateMutation = useUpdateProofingTitle(titleId, {
 *   onSuccess: () => console.log('Saved!'),
 *   onError: (error) => console.error('Save failed:', error)
 * });
 *
 * updateMutation.mutate({
 *   isbn: '978-3-16-148410-0',
 *   supplierId: 'supplier-123'
 * });
 * ```
 */
export const useUpdateProofingTitle = (
  titleId: string,
  options?: UseUpdateProofingTitleOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ProofingTitleUpdateData["body"]) => {
      const response = await proofingTitleUpdate({
        path: { titleId },
        body: data,
      });
      if (!response.data) {
        throw new Error("Failed to update proofing title");
      }
      return response.data;
    },
    onMutate: async (newData) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: titleQueryKeys.detail(titleId) });

      // Snapshot the previous value
      const previousTitle = queryClient.getQueryData(titleQueryKeys.detail(titleId));

      // Optimistically update to the new value
      queryClient.setQueryData(titleQueryKeys.detail(titleId), (old: any) => {
        if (!old) return old;
        return {
          ...old,
          ...newData,
          // Update the updatedAt timestamp optimistically
          updatedAt: new Date().toISOString(),
        };
      });

      // Return context with the previous value for rollback
      return { previousTitle };
    },
    onSuccess: (_data) => {
      // Invalidate queries to refetch fresh data from server
      queryClient.invalidateQueries({ queryKey: titleQueryKeys.detail(titleId) });
      queryClient.invalidateQueries({ queryKey: titleQueryKeys.all() });
      options?.onSuccess?.();
    },
    onError: (error: unknown, _newData, context) => {
      // Rollback to the previous value on error
      if (context?.previousTitle) {
        queryClient.setQueryData(titleQueryKeys.detail(titleId), context.previousTitle);
      }
      console.error("Failed to update proofing title:", error);
      options?.onError?.(error);
    },
  });
};

export interface UseArchiveProofingTitleOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for archiving a completed proofing title.
 *
 * Archives can only be performed on titles with state 'completed'.
 * Once archived, the title is hidden from list views by default but can
 * still be accessed by ID. This action cannot be undone.
 *
 * @param titleId - The ID of the title to archive
 * @param options - Optional callbacks for success and error handling
 *
 * @example
 * ```tsx
 * const archiveMutation = useArchiveProofingTitle(titleId, {
 *   onSuccess: () => console.log('Title archived!'),
 *   onError: (error) => console.error('Archive failed:', error)
 * });
 *
 * archiveMutation.mutate();
 * ```
 */
export const useArchiveProofingTitle = (
  titleId: string,
  options?: UseArchiveProofingTitleOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await proofingTitleArchive({
        path: { titleId },
      });
      if (!response.data) {
        throw new Error("Failed to archive proofing title");
      }
      return response.data;
    },
    onSuccess: () => {
      // Invalidate queries to refetch fresh data
      queryClient.invalidateQueries({ queryKey: titleQueryKeys.detail(titleId) });
      queryClient.invalidateQueries({ queryKey: titleQueryKeys.all() });
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      console.error("Failed to archive proofing title:", error);
      options?.onError?.(error);
    },
  });
};

export interface UseDeleteProofingTitleOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for deleting (soft delete) an empty or incorrect proofing title.
 *
 * Unlike archive (which requires state 'completed'), delete provides a removal
 * path for wrong drafts that would otherwise get stuck. This action is
 * irreversible from the UI: deleted titles are excluded from list views by
 * default.
 *
 * @param titleId - The ID of the title to delete
 * @param options - Optional callbacks for success and error handling
 *
 * @example
 * ```tsx
 * const deleteMutation = useDeleteProofingTitle(titleId, {
 *   onSuccess: () => console.log('Title deleted!'),
 *   onError: (error) => console.error('Delete failed:', error)
 * });
 *
 * deleteMutation.mutate();
 * ```
 */
export const useDeleteProofingTitle = (
  titleId: string,
  options?: UseDeleteProofingTitleOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await proofingTitleDelete({
        path: { titleId },
      });
      if (!response.data) {
        throw new Error("Failed to delete proofing title");
      }
      return response.data;
    },
    onSuccess: () => {
      // Invalidate queries to refetch fresh data
      queryClient.invalidateQueries({ queryKey: titleQueryKeys.detail(titleId) });
      queryClient.invalidateQueries({ queryKey: titleQueryKeys.all() });
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      console.error("Failed to delete proofing title:", error);
      options?.onError?.(error);
    },
  });
};
